/**
 * Base class for binding data to DOM elements using CSS class selectors
 * Inspired by openradtool's approach: declarative DOM updates via class names
 */

export interface BindingOptions {
    /** Root element to search within. Defaults to document */
    root?: Element | Document;
}

export class DomBinder {
    protected root: Element | Document;

    constructor(options: BindingOptions = {}) {
        this.root = options.root || document;
    }

    /**
     * Set text content of elements matching the class selector
     */
    protected setText(className: string, value: string): void {
        const elements = this.root.querySelectorAll(`.${className}`);
        elements.forEach(el => {
            el.textContent = value;
        });
    }

    /**
     * Set value of input elements matching the class selector
     */
    protected setValue(className: string, value: string): void {
        const elements = this.root.querySelectorAll<HTMLInputElement>(`.${className}`);
        elements.forEach(el => {
            el.value = value;
        });
    }

    /**
     * Set attribute on elements matching the class selector
     */
    protected setAttribute(className: string, attribute: string, value: string): void {
        const elements = this.root.querySelectorAll(`.${className}`);
        elements.forEach(el => {
            el.setAttribute(attribute, value);
        });
    }

    /**
     * Add class to elements matching the selector
     */
    protected addClass(className: string, classToAdd: string): void {
        const elements = this.root.querySelectorAll(`.${className}`);
        elements.forEach(el => {
            el.classList.add(classToAdd);
        });
    }

    /**
     * Remove class from elements matching the selector
     */
    protected removeClass(className: string, classToRemove: string): void {
        const elements = this.root.querySelectorAll(`.${className}`);
        elements.forEach(el => {
            el.classList.remove(classToRemove);
        });
    }

    /**
     * Show elements matching the class selector
     */
    protected show(className: string): void {
        this.removeClass(className, 'hidden');
    }

    /**
     * Hide elements matching the class selector
     */
    protected hide(className: string): void {
        this.addClass(className, 'hidden');
    }

    /**
     * Toggle visibility of elements matching the class selector
     */
    protected toggle(className: string): void {
        const elements = this.root.querySelectorAll(`.${className}`);
        elements.forEach(el => {
            el.classList.toggle('hidden');
        });
    }

    /**
     * Enable input elements matching the class selector
     */
    protected enable(className: string): void {
        const elements = this.root.querySelectorAll<HTMLInputElement | HTMLButtonElement>(`.${className}`);
        elements.forEach(el => {
            el.disabled = false;
        });
    }

    /**
     * Disable input elements matching the class selector
     */
    protected disable(className: string): void {
        const elements = this.root.querySelectorAll<HTMLInputElement | HTMLButtonElement>(`.${className}`);
        elements.forEach(el => {
            el.disabled = true;
        });
    }

    /**
     * Add event listener to elements matching the class selector
     */
    protected on<K extends keyof HTMLElementEventMap>(
        className: string,
        event: K,
        handler: (this: HTMLElement, ev: HTMLElementEventMap[K]) => void
    ): void {
        const elements = this.root.querySelectorAll<HTMLElement>(`.${className}`);
        elements.forEach(el => {
            el.addEventListener(event, handler);
        });
    }

    /**
     * Get first element matching the class selector
     */
    protected getElement<T extends HTMLElement = HTMLElement>(className: string): T | null {
        return this.root.querySelector<T>(`.${className}`);
    }

    /**
     * Get all elements matching the class selector
     */
    protected getElements<T extends HTMLElement = HTMLElement>(className: string): NodeListOf<T> {
        return this.root.querySelectorAll<T>(`.${className}`);
    }
}
